import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '../../i18n/LanguageContext';
import { throttleAware } from './responseSafety';
import Banner from './components/Banner';

export function ErrorState({ message, onRetry }) {
  const t = useT();
  return (
    <Banner tone="error">
      <div className="flex items-start justify-between gap-4">
        <span>{t(message)}</span>
        {onRetry && (
          <button onClick={onRetry} className="underline font-medium whitespace-nowrap">
            {t('Try again')}
          </button>
        )}
      </div>
    </Banner>
  );
}

export function Stat({ label, value, hint, tone }) {
  // A figure worth noticing is highlighted in the house blue. Unlike the banners, the tone here is
  // NOT the only signal and is not meant to be: every tile that sets it also passes a `hint` saying
  // what to do about it ("blocked - see below", "reasons listed below", "the list was capped"), so
  // the number still reads correctly with the colour removed.
  const valueTone = tone === 'warn' ? 'text-[#0089ff]' : 'text-[#0f172a]';
  return (
    <div className="bg-white rounded-xl border border-[#e2e8f0] px-4 py-3">
      <div className="text-[12px] text-[#64748b]">{label}</div>
      <div className={`text-[20px] font-bold mt-0.5 ${valueTone}`}>{value}</div>
      {hint && <div className="text-[11px] text-[#94a3b8] mt-0.5">{hint}</div>}
    </div>
  );
}

/** A section container, so every block on the page has the same frame instead of each inventing one. */
export function Card({ title, subtitle, right, children, className = '' }) {
  return (
    <div className={`bg-white rounded-xl border border-[#e2e8f0] ${className}`}>
      {(title || right) && (
        <div className="px-5 py-4 border-b border-[#e2e8f0] flex items-start justify-between gap-4">
          <div>
            <h2 className="text-[15px] font-semibold text-[#0f172a]">{title}</h2>
            {subtitle && <p className="text-[13px] text-[#64748b] mt-0.5">{subtitle}</p>}
          </div>
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

/**
 * One gate, shown pass or fail with the reason the SERVER gave.
 *
 * The reason strings are not written here. They come from the run planner, which is the code that
 * actually refuses - so this cannot drift into reassuring an operator that a gate is open when the
 * thing enforcing it disagrees.
 */
export function Gate({ ok, label, detail }) {
  const t = useT();
  return (
    <li className="flex items-start gap-3 py-2.5">
      <span
        aria-hidden="true"
        className={`material-symbols-outlined text-[20px] leading-5 mt-px ${
          ok ? 'text-[#16a34a]' : 'text-[#94a3b8]'
        }`}
      >
        {ok ? 'check_circle' : 'radio_button_unchecked'}
      </span>
      <span className="min-w-0">
        <span className="block text-[13px] font-medium text-[#0f172a]">
          {t(label)}
          {/* Spelt out for a screen reader, which cannot see that the icon is a tick. */}
          <span className="sr-only">
            {' - '}
            {t(ok ? 'ready' : 'not ready')}
          </span>
        </span>
        {/* The reason comes from the server; a sentence the dictionary knows reads translated,
            anything newer falls through in English. */}
        <span className="block text-[12px] text-[#64748b] break-words">{t(detail)}</span>
      </span>
    </li>
  );
}

/**
 * THE LOADER EVERY TAB ON THIS PAGE SHARES.
 *
 * This existed three times, copied by hand, and the third copy was missing the first-load/refresh
 * split - so the credit-terms tab still blanked a correctly loaded list to a skeleton on every
 * refresh and threw it away entirely on a failure. That is not a coincidence to be fixed three
 * times; it is what happens when a rule lives in three places.
 *
 * The rules, in one place:
 *  - Only the FIRST load may blank the view. `error` replaces the tab, and only a load with nothing
 *    on screen is allowed to set it.
 *  - A refresh that fails reports through `refreshError`, beside the data it failed to replace,
 *    because a rate limit must not cost the operator the page they already had.
 *  - A refresh in flight is visible (`refreshing`) and a second one is dropped, so the button says
 *    what is happening rather than appearing dead for up to the scan timeout.
 *
 * `fetcher` must be a stable reference; the module-level ones below are.
 */
export function useScanLoad(fetcher, fallbackMessage) {
  const [data, setData] = useState(null);
  const [receivedAt, setReceivedAt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshError, setRefreshError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  /** Mirrors `data` so the loader can tell a first load from a refresh without depending on it. */
  const dataRef = useRef(null);
  // React 18 StrictMode double-invokes effects in development, which would fire two concurrent
  // scans and trip the server's one-per-minute limit on a clean first load.
  const inFlight = useRef(false);

  const reload = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    const first = !dataRef.current;
    if (first) setLoading(true);
    else setRefreshing(true);
    setError('');
    setRefreshError('');
    try {
      const next = await fetcher();
      dataRef.current = next;
      setData(next);
      // Wall-clock at the moment the payload arrived, so the server's own timestamp inside it can
      // be advanced by elapsed time rather than compared against this browser's idea of the date.
      setReceivedAt(Date.now());
    } catch (e) {
      const text = throttleAware(e, fallbackMessage);
      if (first) setError(text);
      else setRefreshError(text);
    } finally {
      inFlight.current = false;
      setLoading(false);
      setRefreshing(false);
    }
  }, [fetcher, fallbackMessage]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { data, receivedAt, loading, error, refreshError, refreshing, reload };
}

/**
 * Something changed outside React that the page's correctness depends on - here, the date.
 *
 * A tab left open overnight never re-renders on its own, so nothing notices that the Eastern day
 * has turned over and the figures on screen now describe yesterday. A timer would be the obvious
 * answer and is the wrong one: the operator cannot act on a page they are not looking at, and
 * coming back to it is the moment that matters.
 */
